import { cx } from 'class-variance-authority';
import { cloneElement, forwardRef, ReactElement } from 'react';
import { Box } from '../Box';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';
import * as styles from './styles';
import type { StyleProps } from './styles';
import { Label } from '../Label';
import { InputError } from '../InputError';
import { hasError } from '../utils/error';
import { Button } from '../Button';

type FileInputTypeMap = BaseTypeMap<
  'input',
  Omit<StyleProps, 'intent'> & {
    startAdornment?: ReactElement<{ disabled?: boolean }>;
    endAdornment?: ReactElement<{ disabled?: boolean }>;
    label: string;
    name: string;
    error?: string | string[];
    id?: string;
    fileUploadButtonText?: string;
  }
>;

export type FileInputProps = ComponentProps<FileInputTypeMap>;

export const FileInput: Component<FileInputTypeMap> = forwardRef(
  function FileInput(
    {
      type = 'file',
      className,
      startAdornment,
      endAdornment,
      label,
      disabled,
      name,
      error,
      id,
      fileUploadButtonText,
      ...rest
    },
    ref
  ) {
    return (
      <Box className={className}>
        <Box
          className={cx(
            styles.group({
              error: hasError(error),
            })
          )}
        >
          {startAdornment ? (
            <Box
              className={styles.adornment({
                position: 'left',
              })}
            >
              {cloneElement(startAdornment, { disabled: !!disabled })}
            </Box>
          ) : null}

          <Box className={styles.inputWrapper()}>
            <Box
              as="input"
              {...rest}
              ref={ref}
              type={type}
              className={styles.input({ disabled: !!disabled })}
              disabled={disabled}
              aria-describedby={`${id || name}-desc`}
              name={name}
              id={id || name}
              aria-invalid={!!error ? 'true' : undefined}
              style={{ left: '-9999px' }}
            />
            <Label
              as="label"
              htmlFor={id || name}
              rise={!!rest.value}
              disabled={!!disabled}
              weight="light"
            >
              <Button
                as="span"
                intent="outlined"
                className={styles.fileUploadButton()}
              >
                {fileUploadButtonText}
              </Button>
              {!rest.required ? `${label}` : `${label} *`}
            </Label>
          </Box>
          {endAdornment ? (
            <Box
              className={styles.adornment({
                position: 'right',
              })}
            >
              {cloneElement(endAdornment, { disabled: !!disabled })}
            </Box>
          ) : null}
        </Box>
        <InputError error={error} id={`${name}-desc`} />
      </Box>
    );
  }
);
